import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine, Cell } from "recharts";
import { TrendingUp, Trophy, Flag, Sparkles, History } from "lucide-react";
import { Panel, fmt } from "./StoryParts";
import { buildTrends, buildYearInReview, delta, rangeLabel, monthLabel } from "./trendData";
import YearInReview from "./YearInReview";

const Pill = ({ d, testId }) => {
  if (!d) return null;
  if (d.dir === "flat") return <span className="px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-slate-700/40 border border-slate-600/40 text-slate-300" data-testid={testId}>steady</span>;
  const up = d.dir === "up";
  return (
    <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-bold tabular-nums border ${up ? "bg-lime-400/15 border-lime-400/30 text-lime-300" : "bg-amber-400/10 border-amber-400/25 text-amber-300"}`} data-testid={testId}>
      {up ? "▲ +" : "▼ "}{Math.abs(d.pct).toFixed(Math.abs(d.pct) >= 10 ? 0 : 1)}%
    </span>
  );
};

const Compare = ({ label, now, before, format = fmt, testId }) => {
  const d = delta(now, before);
  return (
    <div className="bg-white/5 rounded-xl p-4 border border-white/10" data-testid={testId}>
      <p className="text-[10px] uppercase tracking-[0.14em] text-slate-400 font-bold mb-1">{label}</p>
      <div className="flex items-baseline gap-2 flex-wrap">
        <p className="text-white text-xl font-bold tabular-nums">{format(now)}</p>
        <Pill d={d} testId={`${testId}-delta`} />
      </div>
      <p className="text-slate-400 text-xs mt-1">was {before == null ? "n/a" : format(before)}</p>
      {d?.dir === "down" && <p className="text-amber-300/90 text-[11px] mt-1">Worth a conversation. Your ad rep can dig into the why.</p>}
    </div>
  );
};

const TrendTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const r = payload[0].payload;
  return (
    <div className="rounded-xl bg-[#062a3a] ring-1 ring-cyan-300/30 px-4 py-3 shadow-xl min-w-[190px]" data-testid="trend-tooltip">
      <p className="text-white text-sm font-semibold mb-1.5">{label}</p>
      {r.impressions > 0 && (
        <p className="flex items-center justify-between gap-4 text-xs py-0.5">
          <span className="flex items-center gap-1.5 text-slate-300"><span className="w-2 h-2 rounded-sm bg-sky-400" />Times your ads were seen</span>
          <span className="text-white font-semibold tabular-nums">{fmt(r.impressions)}</span>
        </p>
      )}
      {r.clicks > 0 && (
        <p className="flex items-center justify-between gap-4 text-xs py-0.5">
          <span className="text-slate-300 pl-3.5">Clicks</span>
          <span className="text-white font-semibold tabular-nums">{fmt(r.clicks)}</span>
        </p>
      )}
      {r.ctr != null && (
        <p className="flex items-center justify-between gap-4 text-xs py-0.5">
          <span className="flex items-center gap-1.5 text-slate-300"><span className="w-2 h-0.5 bg-lime-300" />Click rate</span>
          <span className="text-lime-300 font-semibold tabular-nums">{r.ctr}%</span>
        </p>
      )}
    </div>
  );
};

const TrendChart = ({ chart, benchmark, p }) => {
  const ctrMax = Math.max(...chart.map((c) => c.ctr || 0), benchmark) * 1.2;
  const step = ctrMax > 1.5 ? 0.5 : ctrMax > 0.6 ? 0.2 : 0.1;
  const ctrTop = Math.ceil(ctrMax / step) * step;
  return (
  <div className="h-56" data-testid={`${p}-trend-chart`}>
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={chart} margin={{ top: 8, right: 4, left: -16, bottom: 0 }}>
        <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
        <XAxis dataKey="label" tick={{ fill: "#94a3b8", fontSize: 11 }} />
        <YAxis yAxisId="i" tick={{ fill: "#94a3b8", fontSize: 10 }} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}K` : v)} />
        <YAxis yAxisId="c" orientation="right" tick={{ fill: "#bef264", fontSize: 10 }} tickFormatter={(v) => `${Number(v).toFixed(1)}%`} domain={[0, ctrTop]} ticks={Array.from({ length: Math.round(ctrTop / step) + 1 }, (_, i) => Number((i * step).toFixed(1)))} />
        <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} content={<TrendTooltip />} />
        <Bar yAxisId="i" dataKey="impressions" radius={[3, 3, 0, 0]}>
          {chart.map((c) => <Cell key={c.key} fill={c.current ? "#38bdf8" : "#475569"} fillOpacity={c.current ? 0.8 : 0.6} />)}
        </Bar>
        <Line yAxisId="c" dataKey="ctr" stroke="#bef264" strokeWidth={2} dot={{ r: 3, fill: "#bef264" }} connectNulls type="monotone" />
        <ReferenceLine yAxisId="c" y={benchmark} stroke="#f8fafc" strokeOpacity={0.4} strokeDasharray="4 4" />
      </ComposedChart>
    </ResponsiveContainer>
  </div>
  );
};

const BudgetNote = ({ on, readOnly, onToggle, p }) => (
  <div className="flex flex-wrap items-center gap-3 mb-4">
    {on && <p className="text-slate-200 text-xs bg-white/5 border border-white/15 rounded-lg px-3 py-2" data-testid={`${p}-budget-note`}>Budget changed this period, so click rate is the fairest comparison. Totals naturally rise and fall with spend.</p>}
    {!readOnly && (
      <button onClick={onToggle} className="text-[11px] font-semibold text-sky-300 hover:text-white underline-offset-2 hover:underline" data-testid={`${p}-budget-note-toggle`}>
        {on ? "Remove budget note" : "Budget changed this period? Add a note. Only you see this button."}
      </button>
    )}
  </div>
);

export default function TrendsPanel({ clientId, clientName, API, readOnly, reportStart, reportEnd, endpoint, channel, benchmark, benchmarkLabel, channelLabel, typicalLabel, p, showYearInReview = false, showHighlights = true }) {
  const [data, setData] = useState({ months: [], budget_notes: [] });
  const period = `${reportStart}|${reportEnd}`;
  useEffect(() => {
    axios.get(`${API}/${endpoint}?client_id=${clientId}`).then((r) => setData(r.data)).catch(() => {});
  }, [API, clientId, endpoint]);
  const t = useMemo(() => buildTrends(data.months || [], reportStart, reportEnd), [data.months, reportStart, reportEnd]);
  const yir = useMemo(() => (showYearInReview ? buildYearInReview(data.months || [], reportEnd) : null), [showYearInReview, data.months, reportEnd]);
  if (!t) return null;
  const budgetOn = (data.budget_notes || []).includes(period);
  const toggle = () => axios.post(`${API}/clients/${clientId}/meta-budget-note`, { period, on: !budgetOn, channel })
    .then((r) => setData((d) => ({ ...d, budget_notes: r.data.budget_notes })));
  const base = t.prior;
  const ctrFmt = (v) => `${Number(v).toFixed(2)}%`;
  return (
    <>
    {yir && <YearInReview yir={yir} clientName={clientName} channelLabel={channelLabel} typicalLabel={typicalLabel} benchmark={benchmark} p={p} />}
    <Panel title="How it's trending" icon={TrendingUp} testId={`${p}-trends`} className="mb-6">
      <p className="text-slate-300 text-sm mb-4" data-testid={`${p}-trends-compare-label`}>
        {base ? <><span className="text-white font-semibold">{rangeLabel(t.cur.keys)}</span> compared with <span className="text-white font-semibold">{rangeLabel(base.keys)}</span>{base.count < t.n ? ` (${base.count} of ${t.n} earlier months loaded)` : ""}.</>
          : <>No earlier months loaded yet, so here&apos;s the story since you started.</>}
      </p>
      <BudgetNote on={budgetOn} readOnly={readOnly} onToggle={toggle} p={p} />
      {base && (() => {
        const avg = base.count !== t.cur.count;
        const v = (obj, k) => (avg ? Math.round(obj[k] / obj.count) : obj[k]);
        const sfx = avg ? " / month" : "";
        return (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
          <Compare label="Click rate" now={t.cur.ctr} before={base.ctr} format={ctrFmt} testId={`${p}-trend-ctr`} />
          <Compare label={`Times seen${sfx}`} now={v(t.cur, "impressions")} before={v(base, "impressions")} testId={`${p}-trend-impressions`} />
          <Compare label={`Clicks${sfx}`} now={v(t.cur, "clicks")} before={v(base, "clicks")} testId={`${p}-trend-clicks`} />
          <Compare label={`Returning people${sfx}`} now={v(t.cur, "back_impressions")} before={v(base, "back_impressions")} testId={`${p}-trend-returning`} />
          <Compare label={avg ? "Bonus visits / month" : "Bonus visits, no click"} now={v(t.cur, "view_through")} before={v(base, "view_through")} testId={`${p}-trend-bonus`} />
        </div>
        );
      })()}
      {t.yoy && (
        <p className="text-slate-300 text-sm mb-4" data-testid={`${p}-trends-yoy`}>
          <History className="inline w-4 h-4 text-sky-300 mr-1 -mt-0.5" />
          Same months last year ({rangeLabel(t.yoy.keys)}): click rate {ctrFmt(t.yoy.ctr)} then, {ctrFmt(t.cur.ctr)} now.
        </p>
      )}
      <TrendChart chart={t.chart} benchmark={benchmark} p={p} />
      <div className="flex flex-wrap gap-4 text-[11px] text-slate-400 mt-2 mb-4">
        <span><span className="inline-block w-2.5 h-2.5 rounded-sm bg-sky-400 mr-1" />Bars: times your ads were seen{base ? ", this period" : ""}</span>
        {base && <span><span className="inline-block w-2.5 h-2.5 rounded-sm bg-slate-500 mr-1" />Earlier months</span>}
        <span><span className="inline-block w-2.5 h-0.5 bg-lime-300 mr-1 align-middle" />Line: click rate</span>
        <span><span className="inline-block w-3 border-t border-dashed border-slate-200 mr-1 align-middle" />{benchmarkLabel} ({benchmark}%)</span>
      </div>
      {showHighlights && (<>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {t.best && (
          <div className="flex items-start gap-2 bg-lime-400/5 border border-lime-400/25 rounded-xl p-3" data-testid={`${p}-trend-best-month`}>
            <Trophy className="w-4 h-4 text-lime-300 mt-0.5 flex-shrink-0" />
            <p className="text-slate-200 text-sm"><span className="text-white font-semibold">{monthLabel(t.best.month, true)}</span> was your strongest month: {fmt(t.best.clicks)} clicks at {ctrFmt(t.best.ctr)}.</p>
          </div>
        )}
        <div className="flex items-start gap-2 bg-white/5 border border-white/10 rounded-xl p-3" data-testid={`${p}-trend-since-start`}>
          <Flag className="w-4 h-4 text-sky-300 mt-0.5 flex-shrink-0" />
          <p className="text-slate-200 text-sm">Since {monthLabel(t.sinceStart.first, true)}: <span className="text-white font-semibold">{fmt(t.sinceStart.impressions)} views</span> and <span className="text-white font-semibold">{fmt(t.sinceStart.clicks)} clicks</span> across {t.sinceStart.count} months.</p>
        </div>
      </div>
      {t.freshness.length > 0 && (
        <div className="mt-4 space-y-2" data-testid={`${p}-ad-freshness`}>
          <p className="text-[11px] uppercase tracking-[0.14em] text-slate-400 font-bold">Ad freshness</p>
          {t.freshness.map((a) => (
            <div key={a.name} className="flex flex-wrap items-center gap-x-3 gap-y-1 bg-white/5 rounded-lg px-3 py-2 text-xs">
              <Sparkles className="w-3.5 h-3.5 text-sky-300" />
              <span className="text-slate-200 truncate flex-1 min-w-0" title={a.name}>{a.name}</span>
              <span className="text-slate-400">{a.months} months · {a.first.toFixed(2)}% to {a.latest.toFixed(2)}%</span>
              <span className={`px-2 py-0.5 rounded-full font-bold border ${a.tired ? "bg-amber-400/10 border-amber-400/25 text-amber-300" : "bg-lime-400/15 border-lime-400/30 text-lime-300"}`}>
                {a.tired ? "Might be time for a fresh ad" : "Still going strong"}
              </span>
            </div>
          ))}
        </div>
      )}
      </>)}
    </Panel>
    </>
  );
}
